import { apiClient } from '@/lib/api-client';
import type { ApiSuccess } from '@/lib/api-client';
import type { Warehouse, WarehouseCreateInput, WarehouseUpdateInput } from './types';

/**
 * Warehouse Master API helpers — endpoint `/api/warehouses`.
 *
 * Mirrors `/api/package-types`: the list endpoint accepts an optional
 * `active_only=true` filter so the inbound dialog only shows usable rows.
 * Manager+ roles can mutate.
 */

export interface WarehouseListMeta {
  total: number;
  page: number;
  per_page: number;
}

export interface WarehouseListResult {
  rows: Warehouse[];
  meta: WarehouseListMeta;
}

export interface WarehouseListParams {
  active_only?: boolean;
  /**
   * Physical facility scope. Operational selectors must always pass it: the
   * backend only accepts a warehouse that belongs to the batch's facility, so
   * an unfiltered list offers choices every submit would reject.
   */
  facility_id?: string;
  page?: number;
  per_page?: number;
}

export const listWarehouses = async (
  params: WarehouseListParams = {},
): Promise<WarehouseListResult> => {
  const res = await apiClient.get<ApiSuccess<Warehouse[], WarehouseListMeta>>('/warehouses', {
    params,
  });
  const rows = res.data.data ?? [];
  return {
    rows,
    meta: res.data.meta ?? {
      total: rows.length,
      page: params.page ?? 1,
      per_page: params.per_page ?? rows.length,
    },
  };
};

export const createWarehouse = async (body: WarehouseCreateInput): Promise<Warehouse> => {
  const res = await apiClient.post<ApiSuccess<Warehouse>>('/warehouses', body);
  return res.data.data;
};

export const updateWarehouse = async (
  id: string,
  body: WarehouseUpdateInput,
): Promise<Warehouse> => {
  const res = await apiClient.put<ApiSuccess<Warehouse>>(`/warehouses/${id}`, body);
  return res.data.data;
};

export const reorderWarehouses = async (orderedIds: string[]): Promise<Warehouse[]> => {
  const res = await apiClient.post<ApiSuccess<Warehouse[]>>('/warehouses/reorder', {
    ordered_ids: orderedIds,
  });
  return res.data.data;
};

export const deleteWarehouse = async (id: string): Promise<void> => {
  await apiClient.delete(`/warehouses/${id}`);
};

/** Permanent removal. Only succeeds for a deactivated, unreferenced warehouse. */
export const purgeWarehouse = async (id: string): Promise<void> => {
  await apiClient.delete(`/warehouses/${id}/purge`);
};

export const activateWarehouse = async (id: string): Promise<Warehouse> => {
  const res = await apiClient.put<ApiSuccess<Warehouse>>(`/warehouses/${id}`, {
    active_flag: true,
  });
  return res.data.data;
};
